import { cloneDeep, isNumber, mapValues, set } from "lodash";
import { BotAmountData } from "@shared/features/bots/types";
import {
  AmountType,
  ApiGeneralDataResponse,
  ApiTags,
  BacktestingRunType,
  BaseBotData,
  BaseGetBotGraphDataRequest,
  BasicBotData,
  BotConfigurations,
  BotData,
  BotGraphsData,
  BotGraphType,
  BotOptions,
  BotsGraphsData,
  BotStatus,
  BotTag,
  BotTemplateData,
  BotType,
  ConfigurationItem,
  Direction,
  DisplayCurrency,
  FiltersData,
  FiltersSliceState,
  FilterType,
  FormattedBotsGraphsData,
  FormattedChartDataPoint,
  FormTrailingStopData,
  GetBacktestingRunsRequest,
  GetBotsNamesRequest,
  MarginTypes,
  NumberOption,
  OneClickFollowBotData,
  OrderBaseline,
  OrderTypes,
  PrecisionConfig,
  RawCustomStrategyData,
  RawLineChartDataPoint,
  SortingOrder,
  Status,
  StrategyData,
  StrategyStyleType,
  Timeframe,
  TimeRange,
  UpdatedRawChartData,
} from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import {
  consolidateChartDate,
  formatTimeSeriesChartData,
} from "@shared/utils/charts";
import {
  DISPLAY_CURRENCIES,
  LEVERAGE_EXCHANGES,
  LONG_SHORT_TO_DISPLAY,
  MARGIN_TYPE_TO_DISPLAY,
} from "@shared/common/constants";
import {
  addUuidToConfigurationItems,
  convertValues,
  getAllConfigurationItems,
  getChildren,
  getItemByName,
  getItemValue,
  getName,
  getUuidToItems,
} from "@shared/features/configuration/utils";
import {
  testNumberListFilter,
  testRangeFilter,
  testStringListFilter,
  updateBotsFilters,
} from "@shared/features/filters/utils";
import { Color } from "@shared/common/Themes/types";
import {
  BOT_STATUS_TO_HIDE,
  BOT_TYPE_TO_DISPLAY_NAME,
  BOT_TYPE_TO_FILTERS_SLICE,
  GENERAL_PUBLIC_BOT_ERROR_MESSAGE,
  INVALID_PUBLIC_BOT_MESSAGE,
  PRIVATE_PUBLIC_BOT_MESSAGE,
  TRAILING_TOP_ITEM_NAME_TO_DATA_FIELD,
  USING_CHANNEL_SETTING,
} from "@shared/features/bots/constants";
import {
  BaseBotFormValues,
  ChannelValue,
  EntryGraceBaseline,
  LeverageType,
  MultiSymbolMarginData,
  MultiValue,
  OverrideType,
  StrategyBaseline,
  SymbolMarginData,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import {
  ALL_OVERRIDES_TOGGLE_FIELDS,
  channelValue,
  DEFAULT_SIGNALS_BOT_STRATEGY,
  DEFAULT_SIGNALS_CONFIGURATION_STRATEGY,
  DEFAULT_STOP,
  DEFAULT_TAKE_PROFIT,
  ENTRY_GRACE_BASELINE_TO_DISPLAY_TEXT,
  off,
  TOGGLE_FIELD_TO_OVERRIDE_KEY,
  WITHOUT_OFF_TOGGLE_FIELDS,
  PRICE_ADJUSTMENT_BASELINE_TITLE,
  MARGIN_GLOBAL_SETTINGS_KEY,
} from "@shared/features/bots/BotForm/common/constants";
import { isSymbol } from "@shared/types/utils";
import {
  CUSTOM_STRATEGIES_ITEM_NAME,
  DCA_STRATEGIES_ITEM_NAME,
} from "@shared/features/configuration/constants";
import { findLastNonZeroValue } from "@shared/utils/arrays";
import { Dispatch } from "@reduxjs/toolkit";
import { sharedLib } from "@shared/services/shared.service";
import { formatDate } from "@shared/utils/date";

export const getIsPercentAmountType = (
  amountType: BaseBotFormValues["amountType"]
) => {
  return (
    !isSymbol(amountType) &&
    amountType &&
    [AmountType.RISK_PERCENTAGE, AmountType.PERCENTAGE].includes(amountType)
  );
};

export const getAmountDisplay = (
  amountType: AmountType,
  amountValue: number,
  isAdminConfiguration?: boolean
) => {
  if (isAdminConfiguration && !amountType)
    return OverrideType.USER_CONFIGURATION;
  if (getIsPercentAmountType(amountType))
    amountValue = toFixed(amountValue * 100, 2);
  const suffix = getAmountTypeSuffix(amountType, true);
  return `${amountValue}${suffix}`;
};

export const getBotAmountData = (
  botData: Pick<BaseBotData, "options"> | BasicBotData
) => {
  const isUpdatedData =
    "amount_type" in botData &&
    "amount_value" in botData &&
    botData.amount_type &&
    botData.amount_value;
  const amountType = isUpdatedData
    ? botData.amount_type
    : (getOptionsItemValue(botData, "Amount Type") as AmountType);
  const amountValue = isUpdatedData
    ? botData.amount_value
    : getOptionsItemValue(botData, "Amount Value") || 0;
  return {
    amountType,
    amountValue,
  };
};

export const getBotAmountDisplay = (
  botData: Pick<BaseBotData, "options"> | BasicBotData,
  isAdminConfiguration?: boolean
) => {
  const { amountType, amountValue } = getBotAmountData(botData);
  return getAmountDisplay(amountType, amountValue, isAdminConfiguration);
};

export const getOptionsItem = (
  botData: Pick<BaseBotData, "options"> | BasicBotData,
  optionsKey: keyof BotOptions
) => {
  return "options" in botData ? botData.options?.[optionsKey] : undefined;
};

export const getOptionsItemValue = (
  botData: Pick<BaseBotData, "options"> | BasicBotData,
  optionsKey: keyof BotOptions
) => {
  return getOptionsItem(botData, optionsKey)?.[1];
};

export const getAmountTypeSuffix = (
  amountType: AmountType | undefined | MultiValue,
  isLongRiskDisplay?: boolean
) => {
  let suffix: string = "";
  if (amountType === AmountType.FIXED_USD_AMOUNT) suffix = " USD";
  else if (amountType === AmountType.FIXED_BTC_AMOUNT) suffix = " BTC";
  else if (amountType === AmountType.PERCENTAGE) suffix = "%";
  else if (amountType === AmountType.RISK_PERCENTAGE)
    suffix = `%${isLongRiskDisplay ? " Risk" : ""}`;
  return suffix;
};

export const changeGraphDataBaseline = (
  graphData: RawLineChartDataPoint[],
  yValueIndex: number,
  totalChange: number
) => {
  const lastPointValue =
    (graphData.length > 0 && graphData[graphData.length - 1][yValueIndex]) || 0;
  const baselineValue = totalChange - lastPointValue;

  return graphData.map((dataPoint) => {
    const updatedDataPoint: RawLineChartDataPoint = [...dataPoint];
    updatedDataPoint[yValueIndex] += baselineValue;
    return updatedDataPoint;
  });
};

export const changeBotsGraphDataBaseline = (
  graphData: RawLineChartDataPoint[],
  yValueIndex: number,
  filteredBots: BotData[] | BasicBotData[],
  displayCurrency: DisplayCurrency
) => {
  let totalChange = 0;
  filteredBots?.forEach((bot) => {
    if (bot.bot_type === BotType.GRID) {
      totalChange += bot.realised_pnl?.[displayCurrency] || 0;
    } else {
      totalChange +=
        bot.change?.[displayCurrency === DISPLAY_CURRENCIES.BTC ? 1 : 0] || 0;
    }
  });
  return changeGraphDataBaseline(graphData, yValueIndex, totalChange);
};

export const filterBots = (
  botsData: BasicBotData[] | undefined,
  filtersData: FiltersData
) => {
  return botsData?.filter((botData) => {
    const botDirection = botData.reverse ? Direction.SHORT : Direction.LONG;
    const botStatus =
      botData.status === BotStatus.ACTIVE ? Status.ACTIVE : Status.INACTIVE;
    const validSymbols =
      botData.bot_type === BotType.SIGNAL ||
      (!!botData.symbols &&
        testStringListFilter(
          filtersData[FilterType.SYMBOLS]?.value,
          botData.symbols?.[0]
        ));
    return (
      validSymbols &&
      testStringListFilter(
        filtersData[FilterType.CLIENTS]?.value,
        botData.client_name
      ) &&
      testStringListFilter(
        filtersData[FilterType.LONG_SHORT]?.value,
        botDirection
      ) &&
      testStringListFilter(
        filtersData[FilterType.MARGIN_TYPE]?.value,
        MARGIN_TYPE_TO_DISPLAY[botData.margin_type || MarginTypes.ISOLATED]
      ) &&
      testRangeFilter(
        filtersData[FilterType.LEVERAGE]?.value,
        botData.leverage || 1
      ) &&
      testStringListFilter(filtersData[FilterType.STATUS]?.value, botStatus) &&
      testRangeFilter(
        filtersData[FilterType.TIME_FRAME]?.value?.range,
        botData.created_at
      ) &&
      (botData.bot_type !== BotType.SIGNAL ||
        testNumberListFilter(
          filtersData[FilterType.MULTI_GROUPS]?.value,
          botData.sub_group_id
        ))
    );
  });
};

export const getFormattedBotsGraphsData = (
  bots: BasicBotData[] | undefined,
  displayCurrency: DisplayCurrency,
  precisionConfig: PrecisionConfig | undefined,
  botsGraphsData: BotsGraphsData | undefined,
  timeRange: TimeRange,
  useUnixTimestamp?: boolean
): FormattedBotsGraphsData => {
  if (!botsGraphsData || !precisionConfig || !bots || bots.length === 0) {
    return {
      accumulatedProfit: [],
      profitByDay: [],
    };
  }

  const precision =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? precisionConfig.btc_amount
      : precisionConfig.usd_amount;
  const yValueIndex = displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1;
  const filteredBotsIds = new Set(bots.map((botData) => botData.id));

  const filteredGraphData = Object.keys(botsGraphsData)
    .filter((key) => filteredBotsIds.has(parseInt(key)))
    .reduce((obj: BotsGraphsData, key) => {
      obj[parseInt(key)] = botsGraphsData[parseInt(key)];
      return obj;
    }, {});

  const getBotsGraphData = (
    graphType: "accumulated_profit" | "profit_by_day"
  ) =>
    Object.values(filteredGraphData).map((botGraphsData: BotGraphsData) => {
      return botGraphsData[graphType][timeRange];
    });

  const getFormattedGraphData = (
    graphType: "accumulated_profit" | "profit_by_day"
  ) => {
    const botsGraphData = getBotsGraphData(graphType);
    const consolidatedData = consolidateChartDate(botsGraphData);
    let graphData: RawLineChartDataPoint[] = consolidatedData.map(
      (dataPoint) => {
        if (typeof dataPoint[1] === "number")
          return dataPoint as RawLineChartDataPoint;
        return [dataPoint[0], ...dataPoint[1]];
      }
    );

    if (graphType === "accumulated_profit") {
      graphData = changeBotsGraphDataBaseline(
        graphData,
        yValueIndex,
        bots,
        displayCurrency
      );
    }

    return formatTimeSeriesChartData(
      graphData,
      yValueIndex,
      false,
      undefined,
      precision || 0,
      useUnixTimestamp
    );
  };

  return {
    accumulatedProfit: getFormattedGraphData("accumulated_profit"),
    profitByDay: getFormattedGraphData("profit_by_day"),
  };
};

export const getBotMarginDisplay = (botData: BaseBotData) => {
  const leverage = botData.leverage;
  const marginType = botData.margin_type;

  if (botData.exchange === "FTX Futures") return "From Configuration";
  else if (leverage && marginType) {
    return `${MARGIN_TYPE_TO_DISPLAY[marginType]} ${leverage}x`;
  }
  return null;
};

export const getBotLongShortDisplay = (
  botData: BaseBotData | BasicBotData,
  spotDisplay = "Spot"
) => {
  if (!LEVERAGE_EXCHANGES.has(botData.exchange) || !botData.directions)
    return spotDisplay;
  return (
    botData.directions
      ?.map((direction) => LONG_SHORT_TO_DISPLAY[direction])
      .join(", ") ?? Direction.LONG
  );
};

export const getOverrideValue = (
  bot: BotData | BaseBotData,
  toggleField: ToggleField,
  fieldValue: any,
  isChannelConfiguration?: boolean
) => {
  if (bot.bot_type !== BotType.SIGNAL || isChannelConfiguration)
    return fieldValue;
  if (
    ALL_OVERRIDES_TOGGLE_FIELDS.has(toggleField) ||
    WITHOUT_OFF_TOGGLE_FIELDS.has(toggleField)
  ) {
    const overrideKey = TOGGLE_FIELD_TO_OVERRIDE_KEY[toggleField];
    const shouldOverride = !!overrideKey && bot[overrideKey];
    if (fieldValue === off) return undefined;
    if (!!overrideKey && !shouldOverride && !fieldValue) return channelValue;
  }
  return fieldValue;
};

export const getBotTrailingOrderDisplay = (
  botData: BaseBotData,
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT,
  isChannelConfiguration?: boolean,
  isAdminConfiguration?: boolean
) => {
  const isEntry = orderType === OrderTypes.ENTRY;
  const trailingValue = getConfigurationItemValue(
    botData,
    isEntry ? "Trailing Entry" : "Trailing Take-Profit"
  );
  const parsedValue = getOverrideValue(
    botData,
    isEntry ? ToggleField.TRAILING_ENTRY : ToggleField.TRAILING_TAKE_PROFIT,
    trailingValue,
    isChannelConfiguration
  );
  if (!parsedValue) {
    if (isAdminConfiguration) return OverrideType.USER_CONFIGURATION;
    return isChannelConfiguration ? undefined : "Off";
  } else if (typeof parsedValue === "number") {
    return `${parsedValue}%`;
  } else if (parsedValue === channelValue) {
    return USING_CHANNEL_SETTING;
  }
};

export const getFirstEntryGraceDisplay = (
  botData: BaseBotData,
  hideMissingFields?: boolean
) => {
  const optionsKey = "Entry Grace Percentage";
  const isMissingField =
    hideMissingFields && !(optionsKey in (botData.options || {}));
  if (botData.bot_type !== BotType.SIGNAL || isMissingField) return null;
  const percent = getOptionsItemValue(botData, optionsKey);
  const baseline = botData.entry_grace_baseline;
  if (!percent) {
    return "Off";
  } else if (typeof percent === "number") {
    return {
      percent: `Maximum Price Cap: ${toFixed(percent * 100, 2)}%`,
      baseline: `When to Activate:  ${
        ENTRY_GRACE_BASELINE_TO_DISPLAY_TEXT[
          baseline ?? EntryGraceBaseline.PRICE_REACHED
        ]
      }`,
    };
  }
};

export const getTakeProfitGraceDisplay = (
  botData: BaseBotData,
  hideMissingFields?: boolean
) => {
  const optionsKey = "Take Profit Grace Percentage";
  const isMissingField =
    hideMissingFields && !(optionsKey in (botData.options || {}));
  if (botData.bot_type !== BotType.SIGNAL || isMissingField) return null;
  const percent = getOptionsItemValue(botData, optionsKey);
  if (!percent) {
    return "Off";
  }
  return `${toFixed(percent * 100, 2)}%`;
};

export const getCancelOnProfitBeforeEntryDisplay = (
  botData: BaseBotData,
  hideMissingFields?: boolean,
  isAdminConfiguration?: boolean,
  isChannelConfiguration?: boolean
) => {
  if (hideMissingFields && !("cancel_on_profit_before_entry" in botData))
    return;
  const cancelOnProfitBeforeEntry = botData.cancel_on_profit_before_entry;
  if (
    botData.bot_type === BotType.SIGNAL ||
    isAdminConfiguration ||
    isChannelConfiguration
  ) {
    if (typeof cancelOnProfitBeforeEntry !== "boolean")
      return isAdminConfiguration || isChannelConfiguration
        ? "Dynamic"
        : USING_CHANNEL_SETTING;
    return cancelOnProfitBeforeEntry ? "On" : "Off";
  }
};

export const getBotDefaultOrderDisplay = (
  botData: BaseBotData,
  orderType: OrderTypes.STOP | OrderTypes.TAKE_PROFIT,
  hideMissingFields?: boolean
) => {
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  const isStop = orderType === OrderTypes.STOP;
  const name = isStop ? "Stop" : "Take-Profit";
  const percentKey = `Default ${name} Percentage` as const;
  const percent = getOptionsItemValue(botData, percentKey);
  const baselineKey = `Default ${name} Baseline` as const;
  const baseline = getOptionsItemValue(botData, baselineKey);
  const baselineOptions = botData?.options?.[baselineKey]?.[0];
  const botOptions = botData?.options || {};

  const isMissingField =
    !(baselineKey in botOptions) && !(percentKey in botOptions);
  if (hideMissingFields && isMissingField) return;

  const isBaselineOff =
    !baseline || baseline === OrderBaseline.ORIGINAL_FIRST_ENTRY;
  const baselineDisplay = isBaselineOff
    ? undefined
    : baselineOptions?.find((option) => option.value === baseline)?.name;
  const valueExists = percent || baseline;
  const overrideValue = getOverrideValue(
    botData,
    isStop ? ToggleField.STOP : ToggleField.TAKE_PROFITS,
    baseline
  );

  if ((isSignalsBot && !isStop) || valueExists) {
    const percentDisplay = percent ? toFixed(percent * 100, 2) : undefined;
    let baseline: string;
    if (isBaselineOff) baseline = "Off";
    else if (isSignalsBot && orderType === OrderTypes.TAKE_PROFIT)
      baseline = `${PRICE_ADJUSTMENT_BASELINE_TITLE}: ${baselineDisplay}`;
    else baseline = `Baseline: ${baselineDisplay}`;
    return {
      distance: percentDisplay
        ? `Distance: ${percentDisplay}%`
        : isSignalsBot
        ? USING_CHANNEL_SETTING
        : "Off",
      baseline,
    };
  } else if (isStop && overrideValue === channelValue) {
    return USING_CHANNEL_SETTING;
  } else if (!overrideValue && isStop) {
    return "Off";
  }
};

export const getBotAvailableAmountDisplay = (
  botAmountData: BotAmountData | undefined,
  displayCurrency: DisplayCurrency
) => {
  const availableMainCoinAmount = toFixed(
    botAmountData?.mainCoinAvailableAmount || 0,
    botAmountData?.mainCoinAmountPrecision
  );
  let usdAmount: number | undefined;
  if (botAmountData && (botAmountData?.mainCoin || "") !== "USD") {
    usdAmount = botAmountData.convertMainCoinAmountToDisplayCurrency(
      botAmountData?.mainCoinAvailableAmount || 0,
      displayCurrency,
      true
    );
  }

  return `${availableMainCoinAmount} ${botAmountData?.mainCoin || ""}${
    usdAmount !== undefined ? ` (${usdAmount} ${displayCurrency})` : ""
  }`;
};

export const convertTargetsToPercentages = (targets?: number[]) =>
  targets?.map((target) => toFixed(target * 100, 2));

export const getStrategy = (
  botData: BaseBotData,
  orderType: OrderTypes,
  botType: BotType,
  isChannelConfiguration?: boolean
) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  const isEntry = orderType === OrderTypes.ENTRY;
  const strategyFieldName = isEntry ? "buying_strategy" : "selling_strategy";
  const strategy = isSignalsBot
    ? botData[strategyFieldName]
    : getOverrideValue(
        botData,
        isEntry ? ToggleField.ENTRIES : ToggleField.TAKE_PROFIT_STRATEGY,
        botData[strategyFieldName] || undefined,
        isChannelConfiguration
      );
  const isValid = !isSymbol(strategy) && strategy;
  const isCustom = isValid && "targets" in strategy;
  const isBuiltIn = isValid && "type" in strategy;
  let parsedStrategy: StrategyData | undefined | ChannelValue;
  if (isSymbol(strategy)) {
    parsedStrategy = channelValue;
  } else if (!strategy && isSignalsBot) {
    parsedStrategy = isChannelConfiguration
      ? DEFAULT_SIGNALS_CONFIGURATION_STRATEGY
      : DEFAULT_SIGNALS_BOT_STRATEGY;
  } else if (strategy) {
    const priceDifference =
      strategy.price_difference && strategy.price_difference * 100;
    const numberOfActiveOrders = strategy.number_of_active_orders ?? undefined;
    const baseline =
      strategy.baseline ??
      (isChannelConfiguration ? undefined : StrategyBaseline.PREVIOUS_ORDER);
    const baseStrategyData = {
      price_scale: strategy.price_scale,
      price_difference: priceDifference,
      number_of_active_orders: numberOfActiveOrders,
      number_of_orders: strategy.number_of_orders,
      baseline,
    };
    if (isBuiltIn) {
      parsedStrategy = {
        ...baseStrategyData,
        strategy_style: StrategyStyleType.BUILT_IN,
        builtin_strategy_type: strategy.type,
        name: strategy.name,
      };
    } else if (isCustom) {
      const customStrategy = { ...strategy } as Omit<
        RawCustomStrategyData,
        "strategy_style"
      >;
      parsedStrategy = {
        ...baseStrategyData,
        targets: convertTargetsToPercentages(customStrategy.targets),
        strategy_style: StrategyStyleType.CUSTOM,
      };
      if (customStrategy.targets && !isSymbol(customStrategy.targets)) {
        parsedStrategy.number_of_orders = findLastNonZeroValue(
          customStrategy.targets
        );
      }
    } else {
      const firstOrderPercentage =
        strategy.first_order_percentage &&
        toFixed(strategy.first_order_percentage * 100, 2);
      parsedStrategy = {
        ...baseStrategyData,
        amount_scale: strategy.amount_scale,
        first_order_percentage: firstOrderPercentage ?? undefined,
        strategy_style: StrategyStyleType.DCA,
      };
    }
  }
  if (
    isSignalsBot &&
    !isChannelConfiguration &&
    parsedStrategy &&
    !isSymbol(parsedStrategy)
  ) {
    const strategyKeys = new Set([
      "price_scale",
      "price_difference",
      "number_of_active_orders",
      "number_of_orders",
      "targets",
      "amount_scale",
      "builtin_strategy_type",
      "strategy_style",
    ]);
    const strategy = mapValues(parsedStrategy, (value, key) => {
      if (strategyKeys.has(key) && !value) return channelValue;
      return value;
    }) as StrategyData | ChannelValue | undefined;
    if (strategy && !isSymbol(strategy) && isSymbol(strategy?.price_scale)) {
      strategy.baseline = channelValue;
    }
    return strategy;
  }
  return parsedStrategy;
};

export const getConfigurationItem = (
  botData: { configurations?: BotConfigurations } | undefined,
  configurationKey: keyof BotConfigurations
) => {
  return botData?.configurations?.[configurationKey].data[0];
};

export const getBotTemplateStrategies = (
  botData: { configurations?: BotConfigurations } | undefined,
  orderType: OrderTypes
): ConfigurationItem[] => {
  const strategiesItem = getConfigurationItem(
    botData,
    orderType === OrderTypes.ENTRY ? "Entry Strategy" : "Take-Profit Strategy"
  );
  return getChildren(strategiesItem).filter(
    (item: ConfigurationItem) =>
      ![DCA_STRATEGIES_ITEM_NAME, CUSTOM_STRATEGIES_ITEM_NAME].includes(
        getName(item)
      )
  );
};

export const getConfigurationItemValue = (
  botData: BaseBotData,
  configurationKey: keyof BotConfigurations
) => {
  return getItemValue(getConfigurationItem(botData, configurationKey));
};

export const getBotTrailingStopDisplay = (
  botData: BaseBotData,
  isChannelConfiguration?: boolean,
  isAdminConfiguration?: boolean
) => {
  const trailingStop = botData.trailing_stop;
  const { trigger, percent, type } = trailingStop || {};
  const parsedTrigger = getOverrideValue(
    botData,
    ToggleField.TRAILING_STOP,
    trigger,
    isChannelConfiguration
  );
  // @ts-ignore
  const valueExists = trigger && type && type !== 0; // TODO: type changes to enum from backend
  const isOffValue = trigger === off || percent === off || type === off;
  if (valueExists && !isOffValue) {
    const triggerType = trigger.type;
    let triggerValue = trigger.value;
    if (triggerType === "Percent") {
      triggerValue = toFixed(triggerValue * 100, 2);
    }
    const triggerDisplay = `${
      triggerType === "Target" ? "Target #" : ""
    }${triggerValue}${triggerType === "Percent" ? "%" : ""}`;
    const trailingStopPercent = isNumber(percent)
      ? toFixed(percent * 100, 2)
      : undefined;
    return {
      type: `Type: ${type}`,
      percent:
        trailingStopPercent !== undefined
          ? `Trailing Percent: ${trailingStopPercent}%`
          : undefined,
      trigger: `Trigger: ${triggerDisplay}`,
    };
  }
  if (isChannelConfiguration && !trailingStop) {
    return;
  } else if (!parsedTrigger) {
    return isAdminConfiguration ? OverrideType.USER_CONFIGURATION : "Off";
  } else if (
    botData.bot_type === BotType.SIGNAL &&
    parsedTrigger === channelValue
  ) {
    return USING_CHANNEL_SETTING;
  }
};

export const getFormattedBotGraphData = (
  botGraphData: ApiGeneralDataResponse<UpdatedRawChartData> | undefined,
  args: BaseGetBotGraphDataRequest,
  displayCurrency: DisplayCurrency,
  botData?: BotData,
  totalChangePercent?: number
): FormattedChartDataPoint[] => {
  const { data, precision: precisionConfig } = botGraphData || {};
  if (!data || !precisionConfig) return [];

  let graphData: RawLineChartDataPoint[] = (data[args.time_range] || []).map(
    (dataPoint) => {
      if (typeof dataPoint[1] === "number")
        return dataPoint as RawLineChartDataPoint;
      return [dataPoint[0], ...dataPoint[1]];
    }
  );

  const isPercentGraph = [
    BotGraphType.PROFIT_BY_DAY_PERCENTAGE,
    BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE,
  ].includes(args.graph_type);
  let precision: number | undefined;
  if (isPercentGraph) precision = precisionConfig.general_percent || 2;
  else if (displayCurrency === DISPLAY_CURRENCIES.BTC)
    precision = precisionConfig.btc_amount;
  else precision = precisionConfig.usd_amount;

  const yValueIndex =
    displayCurrency === DISPLAY_CURRENCIES.BTC && !isPercentGraph ? 2 : 1;

  if (args.graph_type === BotGraphType.ACCUMULATING_PROFIT && botData) {
    graphData = changeBotsGraphDataBaseline(
      graphData,
      yValueIndex,
      [botData],
      displayCurrency
    );
  } else if (
    args.graph_type === BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE &&
    totalChangePercent !== undefined
  ) {
    graphData = changeGraphDataBaseline(
      graphData,
      yValueIndex,
      totalChangePercent
    );
  }

  return formatTimeSeriesChartData(
    graphData,
    yValueIndex,
    false,
    isPercentGraph ? "%" : undefined,
    precision || 0
  );
};

export const getActionWarningMessage = (action: string, isBulk?: boolean) => {
  const deactivateBotAdditionalText =
    action === "deactivate" || action === "delete"
      ? `\nPlease note that any existing open bot${
          isBulk ? "s" : ""
        } trades will remain open.`
      : "";
  return `Are you sure you want to ${action} ${
    isBulk ? "these bots" : "this bot"
  }?${deactivateBotAdditionalText}`;
};

export const getSuccessMessage = (
  action: string,
  botType: BotType,
  isBulk?: boolean
) => {
  return `${BOT_TYPE_TO_DISPLAY_NAME[botType]} bot${
    isBulk ? "s" : ""
  } successfully ${action}`;
};

export const getErrorMessage = (
  action: string,
  botType: BotType,
  isBulk?: boolean
) => {
  return `Failed to ${action} ${BOT_TYPE_TO_DISPLAY_NAME[botType]} bot${
    isBulk ? "s" : ""
  }`;
};

export const getDefaultOrder = (
  botData: BaseBotData,
  orderType: OrderTypes.STOP | OrderTypes.TAKE_PROFIT,
  useDefaultValue?: boolean
): [BaseBotFormValues["defaultStop"], boolean, boolean] => {
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  const isStop = orderType === OrderTypes.STOP;
  const name = isStop ? "Stop" : "Take-Profit";
  let percent = getOptionsItemValue(botData, `Default ${name} Percentage`);
  let baseline = getOptionsItemValue(botData, `Default ${name} Baseline`);
  const isOverride = isStop
    ? botData.override_default_stop
    : botData.override_default_take_profit;

  const isSignalsBotTakeProfit = isSignalsBot && !isStop;
  let isDefaultOrderOn = false;
  let isDynamicTakeProfitOn = false;
  let defaultOrder: BaseBotFormValues["defaultStop"] = undefined;
  if (useDefaultValue) {
    defaultOrder = isStop ? DEFAULT_STOP : DEFAULT_TAKE_PROFIT;
  }
  const valueExists = !!percent || !!baseline;
  if (isSignalsBotTakeProfit || valueExists) {
    if (baseline && baseline !== OrderBaseline.ORIGINAL_FIRST_ENTRY) {
      isDynamicTakeProfitOn = true;
    } else {
      baseline = OrderBaseline.AVERAGE_ENTRIES;
    }
    let parsedPercent: string | ChannelValue | undefined = undefined;
    if (isSignalsBotTakeProfit || percent !== undefined) {
      parsedPercent =
        percent !== undefined && percent !== null
          ? toFixed((percent || 0) * 100, 2).toString()
          : channelValue;
    }
    if (parsedPercent) {
      isDefaultOrderOn = true;
    }
    defaultOrder = {
      percent: parsedPercent,
      baseline,
    };
  } else if (isStop && isSignalsBot && !valueExists) {
    defaultOrder = isOverride ? undefined : channelValue;
    isDefaultOrderOn = defaultOrder === channelValue;
  }
  return [defaultOrder, isDefaultOrderOn, isDynamicTakeProfitOn];
};

export const getBotMultiSymbolMarginData = (
  botData: BaseBotData
): [BaseBotFormValues["multiSymbolMarginData"], boolean] => {
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  let isOn = false;
  const rawData = botData.symbol_to_leverage_data;
  let multiSymbolMarginData: BaseBotFormValues["multiSymbolMarginData"] =
    undefined;
  if (isSignalsBot && !rawData) {
    isOn = true;
    multiSymbolMarginData = channelValue;
  } else if (rawData) {
    isOn = true;
    multiSymbolMarginData = mapValues(rawData, (rawSymbolMarginData) => {
      const [marginType, multiplier, leverageType] = rawSymbolMarginData;
      return {
        marginType: marginType ?? channelValue,
        multiplier: multiplier ?? channelValue,
        leverageType: multiplier
          ? leverageType ?? LeverageType.EXACT
          : channelValue,
      };
    }) as BaseBotFormValues["multiSymbolMarginData"];
  }
  return [multiSymbolMarginData, isOn];
};

export const getBotTagsData = (
  botData: BotTemplateData
): { tag: BotTag; color: Color }[] => {
  const botTagToColor: Record<BotTag, Color> = {
    [BotTag.NEW]: "green",
    [BotTag.POPULAR]: "gold",
    [BotTag.TRENDING]: "purple",
  } as const;

  const tagsData: { tag: BotTag; color: Color }[] = [];
  Object.entries(botData.bot_tags || {}).forEach(([tag, isPresent]) => {
    const color = botTagToColor[tag as BotTag] || "primaryBlue";
    if (isPresent && color) {
      tagsData.push({
        tag: (tag.charAt(0).toUpperCase() + tag.slice(1)) as BotTag,
        color: botTagToColor[tag as BotTag],
      });
    }
  });
  return tagsData;
};

export const getOptionNameByValue = (
  optionValue: number | null | undefined,
  array: NumberOption[]
) => {
  return array.find((option) => option.value === optionValue)?.name;
};

export const getOptionValueByName = (
  optionName: string | undefined,
  array: NumberOption[]
) => {
  return array.find((option) => option?.name === optionName)?.value;
};

export const getDirectionAndMarginTag = (botData?: {
  exchange?: string;
  reverse?: boolean;
  margin_type?: MarginTypes;
  leverage?: number;
}) => {
  const isMarginExchange =
    !!botData && !!botData.exchange && LEVERAGE_EXCHANGES.has(botData.exchange);
  if (!isMarginExchange) return "Spot";
  let tagText = botData?.reverse ? "Short" : "Long";
  if (botData?.margin_type)
    tagText = tagText.concat(
      " - ",
      MARGIN_TYPE_TO_DISPLAY[botData.margin_type],
      ` ${botData.leverage}x`
    );
  return tagText;
};

export const getParsedTrailingStopFromConfiguration = (
  configuration?: ConfigurationItem
) => {
  const trailingStopData = {};
  Object.entries(TRAILING_TOP_ITEM_NAME_TO_DATA_FIELD).forEach(
    ([itemName, dataFieldName]) => {
      const item = getItemByName(itemName, configuration);
      if (item) {
        set(trailingStopData, dataFieldName, item.value);
      }
    }
  );
  return trailingStopData as FormTrailingStopData;
};

export const getBotParsedTrailingStop = (bot: BaseBotData) => {
  const configuration = cloneDeep(getConfigurationItem(bot, "Trailing Stop"));
  addUuidToConfigurationItems(configuration);
  const uuidToItem = getUuidToItems(configuration);
  const idToItem = getAllConfigurationItems(configuration);
  convertValues(configuration, cloneDeep(idToItem), Object.values(uuidToItem));
  return getParsedTrailingStopFromConfiguration(configuration);
};

export const getEditBotsInvalidateTags = (botsIds: number[]) => {
  return [
    { type: ApiTags.BOTS, id: "LIST" },
    { type: ApiTags.MAIN_COINS_AVAILABLE_AMOUNT, id: "LIST" },
    ...botsIds.map((botId) => ({ type: ApiTags.BOTS, id: botId })),
  ];
};

export const sortBotsByAmount = (
  a: BotData | BasicBotData,
  b: BotData | BasicBotData,
  sortDirection: SortingOrder
) => {
  const aBotData = getBotAmountData(a);
  const bBotData = getBotAmountData(b);
  return (
    (bBotData.amountType || 0) - (aBotData.amountType || 0) ||
    sortDirection * ((aBotData.amountValue || 0) - (bBotData.amountValue || 0))
  );
};

export const getBotsInactiveAccountMessage = (
  actionName: string,
  isInvalid = true,
  isInactive?: boolean
) => {
  let text = "invalid";
  if (isInvalid && isInactive) text = "invalid or inactive";
  else if (isInactive) text = "inactive";
  return `You can't ${actionName} bots that are linked to an ${text} account.`;
};

export const getBotsInvalidGroupMessage = (actionName: string) => {
  return `This bot cannot be ${actionName} because it is connected to a group you are no longer a member of.`;
};

const updateBotsCache = (
  bots: ApiGeneralDataResponse<BasicBotData[]>,
  updatedBot: BasicBotData,
  dispatch: Dispatch<any>,
  botType: BotType
) => {
  if (bots) {
    const botsData = bots.data;
    if (!botsData) return;
    const botIndex = botsData.findIndex(
      (bot: BasicBotData) => bot.id === updatedBot.id
    );
    if (!BOT_STATUS_TO_HIDE.includes(updatedBot.status)) {
      if (botIndex === -1) botsData.push(updatedBot);
      else botsData[botIndex] = updatedBot;
      const filtersSlice = BOT_TYPE_TO_FILTERS_SLICE[botType];
      dispatch(
        filtersSlice.actions.updateFilters({
          filtersUpdateFunction: (state: FiltersSliceState) =>
            updateBotsFilters(state, updatedBot, botsData),
        })
      );
    } else if (botIndex >= 0) {
      botsData.splice(botIndex, 1);
    }
  }
};

export const updateBotsData = (
  updatedBots: BasicBotData | BasicBotData[],
  dispatch: Dispatch<any>,
  botType: BotType
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBotsData",
    {
      bot_type: botType,
    },
    (bots) => {
      if (Array.isArray(updatedBots)) {
        updatedBots.forEach((updatedBot) =>
          updateBotsCache(
            bots as ApiGeneralDataResponse<BasicBotData[]>,
            updatedBot,
            dispatch,
            botType
          )
        );
      } else {
        updateBotsCache(
          bots as ApiGeneralDataResponse<BasicBotData[]>,
          updatedBots,
          dispatch,
          botType
        );
      }
    }
  );
};

export const getBotOneClickFollowCacheKey = (
  bot: BasicBotData | OneClickFollowBotData
) => `oneClickDefault-${bot.id}`;

export const getPublicBotErrorTitle = (errors?: {
  isInvalidLink: boolean;
  isPrivate: boolean;
  generalError: boolean;
}) => {
  if (errors?.isInvalidLink) return "Invalid Bot Link";
  if (errors?.isPrivate) return "Bot Not Valid";
  if (errors?.generalError) return "Something Went Wrong";
};

export const getPublicBotErrorMessage = (errors?: {
  isInvalidLink: boolean;
  isPrivate: boolean;
  generalError: boolean;
}) => {
  if (errors?.isInvalidLink) return INVALID_PUBLIC_BOT_MESSAGE;
  if (errors?.isPrivate) return PRIVATE_PUBLIC_BOT_MESSAGE;
  if (errors?.generalError) return GENERAL_PUBLIC_BOT_ERROR_MESSAGE;
};

export const getDisplayedTimeframe = (timeframe?: Timeframe) => {
  return `${formatDate(timeframe?.start, false, true)} - ${formatDate(
    timeframe?.end,
    false,
    true
  )}`;
};

export const updateGetBacktestingRuns = (
  newBacktestingRun: BacktestingRunType,
  args?: GetBacktestingRunsRequest
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBacktestingRuns",
    args,
    (BacktestingRunsData) => {
      BacktestingRunsData?.data.unshift(newBacktestingRun);
    }
  );
};

export const removeBacktestingFromList = (
  backtestingRunId: number,
  args?: GetBacktestingRunsRequest
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBacktestingRuns",
    args,
    (BacktestingRunsData) => {
      const index = BacktestingRunsData.data.findIndex(
        (run) => run.id === backtestingRunId
      );
      BacktestingRunsData.data.splice(index, 1);
    }
  );
};

export const updateGetBotsNames = (
  botId: number,
  botName: string,
  args: GetBotsNamesRequest
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBotsNames",
    args,
    (botsNames) => {
      Object.assign(botsNames || {}, { [botId]: botName });
    }
  );
};

export const removeBotNameFromList = (
  botId: number,
  args: GetBotsNamesRequest
) => {
  return sharedLib.sharedApi.bots.util.updateQueryData(
    "getBotsNames",
    args,
    (botsNames) => {
      delete botsNames?.[botId];
    }
  );
};

const appendIndexToName = (name?: string, index?: number) => {
  return `${name}${index ? ` #${index}` : ""}`;
};

export const makeNameUnique = (
  name: string,
  botsNames?: Record<number, string>
) => {
  const strIndexMatch = name.match(new RegExp(/\s#(\d+)$/))?.[1];
  let index = strIndexMatch ? Number(strIndexMatch) : 1;
  const strippedName = name.replace(new RegExp(/\s#\d+$/), "");
  let defaultName = name;
  const names = new Set(Object.values(botsNames || {}));
  while (names.has(defaultName)) {
    defaultName = appendIndexToName(strippedName, index);
    index += 1;
  }
  return defaultName;
};

export const getDefaultMarginData = (): SymbolMarginData => ({
  marginType: channelValue,
  multiplier: channelValue,
  leverageType: channelValue,
});

export const getDefaultGlobalSettings = (): MultiSymbolMarginData => {
  return { [MARGIN_GLOBAL_SETTINGS_KEY]: getDefaultMarginData() };
};

export const getHasCustomSymbols = (data: MultiSymbolMarginData) => {
  return Object.keys(data).some(
    (symbol) => symbol !== MARGIN_GLOBAL_SETTINGS_KEY
  );
};
